import {
  FormItem,
  Gap,
  ISelectMiniOption,
  SelectMini,
  LinkText,
  Icon,
  Span,
  useTheme,
} from '@salecycle/ui-component-library';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CURRENCY_ITEMS } from 'core/constants/CurrenciesConstants';
import { DateRangeOptions, ComparisonTypeOptions, DATE_DISPLAY_FORMAT } from '../../data/ReportableDataConsts';
import { DashboardGranularity, DashboardCurrency } from '../../entities';
import { getGranularityForDateRange } from '../../utils/mapping';
import { DateRangeModal } from '../modals/DateRangeModal';
import { BarBlockElement, BarItemElement, BarSectionElement, defaultBarMixin } from '../styles';
import { getStartAndEndDates, timeNow } from '../../utils/dates';
import { useReportableDataDispatch, useReportableDataState } from '../../context/UseReportableDataContext';
import { setBaseCurrencyAction, setGranularityAction } from '../../actions/graphActions';

const TimeTopBarElement = styled.div`
  ${defaultBarMixin}
  border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
  position: relative;
`;

const Dates = styled.button`
  align-items: center;
  appearance: none;
  background: none;
  border-radius: 0;
  border: none;
  box-shadow: none;
  display: flex;
  font-family: inherit;
  font-size: 100%;
  font-weight: 600;
  line-height: 1.21;
  margin: 0;
  overflow: visible;
  padding: 0;
  text-align: inherit;
  text-transform: none;

  &:focus {
    outline-offset: -4px;
  }
`;

export const TimeTopBar: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();

  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [datesValue, setDatesValue] = useState('');
  const [comparisonDatesValue, setComparisonDatesValue] = useState('');

  const {
    queryParameters: { dateRange, dateRangeOffset, comparisonDateRange, comparisonDateRangeOffset, comparisonType },
    graphParameters: { baseCurrency, comparisonEnabled, granularity, timezone },
  } = useReportableDataState();
  const dispatch = useReportableDataDispatch();

  // Updates the value displayed for the dates selector
  useEffect(() => {
    const time = timeNow(timezone);
    const { startDate, endDate } = getStartAndEndDates(dateRange, dateRangeOffset, time);
    const { startDate: comparisonStartDate, endDate: comparisonEndDate } = getStartAndEndDates(
      comparisonDateRange,
      comparisonDateRangeOffset,
      time,
    );

    const description =
      dateRangeOffset === 0
        ? DateRangeOptions(t).find(option => option.value === String(dateRange))?.label ??
          DateRangeOptions(t).find(option => option.value === 'custom').label
        : DateRangeOptions(t).find(option => option.value === 'custom').label;
    const from = startDate.format(DATE_DISPLAY_FORMAT);
    const to = endDate.format(DATE_DISPLAY_FORMAT);

    const comparisonDescription = comparisonEnabled
      ? ComparisonTypeOptions(t).find(option => option.value === comparisonType).label
      : '';
    const comparisonFrom = comparisonEnabled ? comparisonStartDate.format(DATE_DISPLAY_FORMAT) : '';
    const comparisonTo = comparisonEnabled ? comparisonEndDate.format(DATE_DISPLAY_FORMAT) : '';

    const isSingleDay = dateRange <= 1;
    const context = isSingleDay ? 'single' : null;

    setDatesValue(
      t('timeTopBar.dates.value', {
        description,
        from,
        to,
        context,
      }),
    );

    comparisonEnabled
      ? setComparisonDatesValue(
          t('timeTopBar.dates.value', {
            description: comparisonDescription,
            from: comparisonFrom,
            to: comparisonTo,
            context,
          }),
        )
      : setComparisonDatesValue('');
  }, [
    t,
    dateRange,
    dateRangeOffset,
    timezone,
    comparisonDateRange,
    comparisonDateRangeOffset,
    comparisonEnabled,
    comparisonType,
  ]);

  const currencyOptions = CURRENCY_ITEMS;
  const currencyValue = currencyOptions.find(currencyOption => currencyOption.value === baseCurrency);

  const granularityOptions: ISelectMiniOption[] = getGranularityForDateRange(dateRange, t);
  const granularityValue = granularityOptions.find(granularityOption => granularityOption.value === granularity);

  const handleGranularityChanged = (option: ISelectMiniOption) => {
    if (granularity !== option.value) {
      setGranularityAction(dispatch, option.value as DashboardGranularity);
    }
  };

  const handleBaseCurrencyChanged = (option: ISelectMiniOption) => {
    if (baseCurrency !== option.value) {
      setBaseCurrencyAction(dispatch, option.value as DashboardCurrency);
    }
  };

  const toggleDateRangeModal = () => {
    setIsDateRangeModalOpen(!isDateRangeModalOpen);
  };

  return (
    <>
      <DateRangeModal isOpen={isDateRangeModalOpen} toggleModal={toggleDateRangeModal} />

      <TimeTopBarElement data-testid="TimeTopBar">
        <BarSectionElement>
          <BarItemElement>
            <FormItem
              isMini={true}
              spacingBelow={false}
              label={`${t('timeTopBar.dates.label')}:`}
              tooltipHeading={t('timeTopBar.dates.tooltipHeading')}
              tooltipDescription={t('timeTopBar.dates.tooltipDescription')}
            >
              <Dates aria-label={t('timeTopBar.dates.ariaLabel')} onClick={toggleDateRangeModal}>
                <LinkText>
                  {datesValue}
                  {comparisonEnabled ? ' ' : ''}
                </LinkText>

                {comparisonEnabled && (
                  <>
                    <Gap left={5} />

                    <Span color={theme.vars.newColor.darkGS2} bold={true}>
                      {t('timeTopBar.dates.vs')}{' '}
                    </Span>

                    <Gap left={5} />

                    <LinkText>{comparisonDatesValue}</LinkText>
                  </>
                )}

                <Gap left={15} />
                <Icon icon="angle-down" color={theme.vars.newColor.darkGS2} />
              </Dates>
            </FormItem>
          </BarItemElement>
          <BarItemElement>
            <FormItem
              isMini={true}
              spacingBelow={false}
              label={`${t('timeTopBar.dataPoints.label')}:`}
              tooltipHeading={t('timeTopBar.dataPoints.tooltipHeading')}
              tooltipDescription={t('timeTopBar.dataPoints.tooltipDescription')}
            >
              <SelectMini
                ariaLabel={t('timeTopBar.dataPoints.label')}
                onChange={handleGranularityChanged}
                value={granularityValue}
                options={granularityOptions}
              />
            </FormItem>
          </BarItemElement>
        </BarSectionElement>
        <BarSectionElement>
          <BarItemElement>
            <BarBlockElement>
              <FormItem
                isMini={true}
                spacingBelow={false}
                label={`${t('timeTopBar.baseCurrency.label')}:`}
                tooltipHeading={t('timeTopBar.baseCurrency.tooltipHeading')}
                tooltipDescription={t('timeTopBar.baseCurrency.tooltipDescription')}
              >
                <SelectMini
                  ariaLabel={t('timeTopBar.baseCurrency.label')}
                  menuPlacement="right"
                  onChange={handleBaseCurrencyChanged}
                  value={currencyValue}
                  options={currencyOptions}
                  searchable={true}
                />
              </FormItem>
            </BarBlockElement>
          </BarItemElement>
        </BarSectionElement>
      </TimeTopBarElement>
    </>
  );
};
